import React, { useEffect, useRef, useState } from "react";

import { NavigateFunction, useNavigate } from "react-router-dom";
import useDashboardStore from "store/dashboard-store";
import useScadaStore from "store/scada-store";
import { ScadaPolygon } from "../scadaTypes";
import { safeNavigate } from "../utils/scadaUtils";

type ScadaPolygonProps = {
  scadaId: string | undefined;
};

const ScadaPolygons: React.FC<ScadaPolygonProps> = ({ scadaId }) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [polygons, setPolygons] = useState<ScadaPolygon[]>([]);
  const [dragging, setDragging] = useState<{
    polygonIndex: number | null;
    pointIndex: number | null;
  }>({ polygonIndex: null, pointIndex: null });

  let navigate: NavigateFunction | null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate();
  } catch (_) {
    navigate = null;    
  }

  const svgRef = useRef<SVGSVGElement>(null);
  const scadaPolygons = useScadaStore(state =>
    scadaId ? (state.scadaMap.get(scadaId)?.polygons ?? []) : []
  );
  const updatePolygons = useScadaStore(state => state.updatePolygons);
  const isEditMode = useDashboardStore(state => state.isEditMode);

  useEffect(() => {
    const resize = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [svgRef.current]);

  useEffect(() => {
    setPolygons(scadaPolygons);
  }, [scadaPolygons]);

  {
    const percentToPixel = (point: { x: number; y: number; id: string }) => ({
      id: point.id,
      x: (point.x / 100) * size.width,
      y: (point.y / 100) * size.height,
    });

    const pixelToPercent = (x: number, y: number) => ({
      x: Math.round((x / size.width) * 10000) / 100,
      y: Math.round((y / size.height) * 10000) / 100,
    });

    const handleMouseDown =
      (polygonIndex: number, pointIndex: number) =>
      (e: React.MouseEvent<SVGCircleElement, MouseEvent>) => {
        e.stopPropagation();
        updatePolygons(scadaId!, polygons);
        setDragging({ polygonIndex, pointIndex });
      };

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      if (dragging.polygonIndex === null) return;

      const svg = svgRef.current;
      if (!svg || dragging.pointIndex === null) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const cursor = pt.matrixTransform(svg.getScreenCTM()?.inverse());

      const updated = [...polygons];
      updated[dragging.polygonIndex].points[dragging.pointIndex] = {
        id: updated[dragging.polygonIndex].points[dragging.pointIndex].id,
        ...pixelToPercent(cursor.x, cursor.y),
      };
      setPolygons(updated);
    };

    const handleMouseUp = () => {
      setDragging({ polygonIndex: null, pointIndex: null });
    };
    
    return (
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        className="scada-svg-polygons"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {size.width > 0 &&
          polygons.map(({ id, points, redirectTo, color, title }, polygonIndex) => {
            const pixelPoints = points.map(percentToPixel);
            const pointsAttr = pixelPoints.map(p => `${p.x},${p.y}`).join(" ");
            return (
              <g key={id}>
                <polygon
                  points={pointsAttr}
                  fill={color}
                  onClick={() => safeNavigate(navigate, redirectTo)}
                  className="scada-polygon"
                  data-tooltip-id="scada-polygon-tooltip"
                  data-tooltip-content={title?.trim()}
                />
                {isEditMode &&
                  pixelPoints.map((p, pointIndex) => (
                    <circle
                      key={p.id}
                      cx={p.x}
                      cy={p.y}
                      r={4}
                      fill="white"
                      stroke="black"
                      onMouseDown={handleMouseDown(polygonIndex, pointIndex)}
                      style={{ cursor: "move" }}
                    />
                  ))}
              </g>
            );
          })}
      </svg>
    );
  }
};

export default ScadaPolygons;
